import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Modal from '../../shared/components/ui/Modal';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';

import GridWrapper from '../../shared/components/layout/GridWrapper';
import { transformData } from '../../super-admin/utils/transformData';
import AddProject from '../components/forms/AddProject';
import {
  createProject,
  deleteProject,
  editProject,
  getProjectStats,
  handleCheckProjectDelete
} from '../services/api/implementation/impl';
import EditProject from '../components/forms/EditProject';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DateFormat from '../../super-admin/utils/DateFormat';
import { formatIndianCurrency } from '../../shared/utils/CurrencyFormat';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import MetricCard from '../components/ui/MetricCard';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import editIcon from '../../shared/assets/svg/edit.svg';
import deleteIcon from '../../shared/assets/svg/delete.svg';
import {
  BriefcaseBusiness,
  CheckCircle2,
  Eye,
  LayoutGrid,
  List,
  MapPin,
  Plus,
  Search,
  ShieldCheck,
  Timer
} from 'lucide-react';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const ProjectsList = () => {
  /* 
    Hooks
  */
  const navigate = useNavigate();

  /* 
    Global state
  */
  const { projects, refetchProjects } = useProjectsList();

  const companyid = useParams();
  const companyIdInt = parseInt(companyid.id, 10);
  const userId = Cookies.get('userId');

  // Track page view
  useEffect(() => {
    mixpanel.track('Projects List Viewed', {
      user_id: userId,
      company_id: companyIdInt,
      timestamp: new Date().toISOString()
    });
  }, [userId, companyIdInt]);

  /* 
    Local state
  */
  const [isOpenAddProject, setIsOpenAddProject] = useState(false);
  const [isOpenEditProject, setisOpenEditProject] = useState(false);
  const [upShift, setUpShift] = useState(6);
  const [ProjectStaticId, setProjectStaticId] = useState();
  const [ProjectFilter, setProjectFilter] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState([]);
  const [projectQuery, setProjectQuery] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('card');
  /* 
    Data fetching
  */

  useEffect(() => {
    if (!companyIdInt) {
      setData([]);
      return;
    }

    const filteredProjects = (projects || []).filter(
      (project) => project?.company_id === Number(companyIdInt)
    );
    setData(filteredProjects);
  }, [companyIdInt, projects]);

  console.log({ data });

  const { data: projectStats, refetch: refetchProjectStats } = useQuery({
    queryKey: ['project_stats', companyIdInt],
    queryFn: () => getProjectStats(companyIdInt),
    enabled: !!companyIdInt, // Ensure the query only runs if companyIdInt is valid
    onSuccess: (result) => {
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Get Project Stats API Failed', {
          user_id: userId,
          company_id: companyIdInt,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Error loading project statistics'
        );
        return;
      }
      mixpanel.track('Get Project Stats API Success', {
        user_id: userId,
        company_id: companyIdInt,
        total_projects: result?.totalProjects || 0,
        completed_projects: result?.completedProjects || 0,
        incomplete_projects: result?.incompleteProjects || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Get Project Stats API Failed', {
        user_id: userId,
        company_id: companyIdInt,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error(
        err?.response?.data?.message || err?.message || 'Error loading project statistics'
      );
    }
  });

  /* 
    Meta data
  */
  const metaData = [
    {
      _id: 1,
      name: 'Total Projects',
      data: projectStats?.totalProjects || 0,
      icon: BriefcaseBusiness,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      valueColor: 'text-indigo-700',
      border: 'border-indigo-200'
    },
    // {
    //   _id: 2,
    //   type: 'information',
    //   name: 'Total Land Area',
    //   data: `${projectStats?.totalLandArea || 0} Hector`
    // },
    {
      _id: 3,
      name: 'Completed Projects',
      data: projectStats?.completedProjects || 0,
      icon: CheckCircle2,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      valueColor: 'text-green-700',
      border: 'border-green-200'
    },
    {
      _id: 4,
      name: 'Ongoing Projects',
      data: projectStats?.incompleteProjects || 0,
      icon: Timer,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      valueColor: 'text-amber-700',
      border: 'border-amber-200'
    }
  ];

  /* 
    Event handlers
  */
  const handleToggleAddProjectModal = useCallback(() => {
    const isOpening = !isOpenAddProject;
    setIsOpenAddProject(isOpening);

    mixpanel.track('Add Project Modal Toggled', {
      user_id: userId,
      company_id: companyIdInt,
      action: isOpening ? 'opened' : 'closed',
      timestamp: new Date().toISOString()
    });
  }, [isOpenAddProject, userId, companyIdInt]);

  const handleToggleEditProjectModal = useCallback(
    (id) => {
      const isOpening = !isOpenEditProject;
      setProjectStaticId(id);
      setisOpenEditProject(isOpening);

      mixpanel.track('Edit Project Modal Toggled', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: id,
        action: isOpening ? 'opened' : 'closed',
        timestamp: new Date().toISOString()
      });
    },
    [isOpenEditProject, userId, companyIdInt]
  );

  /* 
    Data mutation
  */
  const { mutate: handleOnSubmitAddProject, isLoading: isLoadingNewUserMutation } = useMutation({
    mutationFn: (newProject) => createProject(newProject),
    onMutate: (variables) => {
      setIsSubmitting(true); // Disable the button when mutation starts
      mixpanel.track('Create Project Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_name: variables?.name,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error creating new project', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error creating project');

        mixpanel.track('Create Project Failed', {
          user_id: userId,
          company_id: companyIdInt,
          project_name: variables?.name,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle success
      toast.success('Project created successfully');
      setIsOpenAddProject(false);
      await refetchProjects(); // UI data update
      navigate(`/builder/management/company/projects/${result.id}`);

      mixpanel.track('Create Project Success', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: result?.id,
        project_name: variables?.name || result?.name,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err, variables) => {
      setIsSubmitting(false);
      console.error('Error creating new project', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating project');

      mixpanel.track('Create Project Failed', {
        user_id: userId,
        company_id: companyIdInt,
        project_name: variables?.name,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { mutate: handleEditProject, isLoading: isEditingProject } = useMutation({
    mutationFn: (updatedProject) => {
      // console.log('Entering mutationFn');
      // console.log('updatedProject:', JSON.stringify(updatedProject, null, 2));
      // console.log('ProjectStaticId:', ProjectStaticId);

      if (!updatedProject) {
        throw new Error('updatedProject is null or undefined');
      }

      if (ProjectStaticId == null) {
        throw new Error('ProjectStaticId is null or undefined');
      }

      let parsedLandId;
      try {
        parsedLandId = updatedProject.land_id != null ? parseInt(updatedProject.land_id, 10) : null;
        console.log('Parsed land_id:', parsedLandId);
      } catch (error) {
        console.error('Error parsing land_id:', error);
        throw new Error(`Failed to parse land_id: ${error.message}`);
      }

      const projectToEdit = {
        ...updatedProject,
        land_id: parsedLandId
      };

      console.log('Project to edit:', JSON.stringify(projectToEdit, null, 2));

      return editProject(ProjectStaticId, projectToEdit);
    },
    onMutate: (variables) => {
      setIsSubmitting(true); // Disable the button when mutation starts
      mixpanel.track('Edit Project Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: ProjectStaticId,
        project_name: variables?.name,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, variables) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error editing project:', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error editing project');

        mixpanel.track('Edit Project Failed', {
          user_id: userId,
          company_id: companyIdInt,
          project_id: ProjectStaticId,
          project_name: variables?.name,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle success
      console.log('Edit project succeeded');
      toast.success('Project edited successfully');
      setisOpenEditProject(false);
      await refetchProjects(); // UI data update

      mixpanel.track('Edit Project Success', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: ProjectStaticId,
        project_name: variables?.name || result?.name,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err, variables) => {
      setIsSubmitting(false);
      console.error('Error editing project:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error editing project');

      mixpanel.track('Edit Project Failed', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: ProjectStaticId,
        project_name: variables?.name,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  useEffect(() => {
    const normalizedQuery = projectQuery.trim().toLowerCase();
    const nextFiltered = (data || []).filter((project) => {
      const projectName = String(project?.name || '').toLowerCase();
      const projectType = String(project?.project_type || '')
        .trim()
        .toLowerCase();
      const matchesSearch = !normalizedQuery || projectName.includes(normalizedQuery);
      const matchesType = projectTypeFilter === 'all' || projectType === projectTypeFilter;
      return matchesSearch && matchesType;
    });
    setProjectFilter(nextFiltered);
  }, [data, projectQuery, projectTypeFilter]);

  /* 
    Meta data
  */
  const ProjectDataFormat = [
    'id',
    'name',
    'project_type',
    'estimated_budget',
    'start_date',
    'compl_date',
    'actions'
  ];

  /* 
    Helper method calls
  */
  const FilterProjectData = [...(ProjectFilter || [])].sort((a, b) => {
    const dateA = new Date(a.start_date);
    const dateB = new Date(b.start_date);

    // Compare by year, then by month and day
    return dateA - dateB;
  });
  console.log(FilterProjectData);
  const transformedData = FilterProjectData?.map((item) =>
    transformData(
      {
        ...item,
        name: <span className="text-[#0F172A] font-medium">{item?.name || '---'}</span>,
        project_type: item?.project_type || '---',
        start_date: item.start_date === '__' ? '---' : DateFormat(item.start_date),
        compl_date: item.completion_date === '__' ? '---' : DateFormat(item.completion_date),
        estimated_budget:
          item?.estimated_budget && !isNaN(Number(item.estimated_budget))
            ? Number(item.estimated_budget)
            : '0'
      },
      ProjectDataFormat
    )
  );

  // console.log(ProjectFilter, data);

  const projectTypeOptions = [
    'all',
    ...new Set(
      (data || [])
        .map((project) => String(project?.project_type || '').trim().toLowerCase())
        .filter(Boolean)
    )
  ];
  const hasProjects = FilterProjectData.length > 0;

  /* 
    Event handlers
  */
  const handleNavigateProject = useCallback(
    (id) => {
      mixpanel.track('Project Navigation Clicked', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: id,
        timestamp: new Date().toISOString()
      });
      navigate(`/builder/management/company/projects/${id}`);
    },
    [navigate, userId, companyIdInt]
  );

  /* 
    Effects
  */
  useEffect(() => {
    refetchProjects();
    refetchProjectStats();
  }, [isOpenAddProject, isOpenEditProject, refetchProjects, refetchProjectStats]);

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckProjectDelete(id),
    onMutate: (id) => {
      setIsSubmitting(true); // Disable the button when mutation starts
      mixpanel.track('Check Project Delete Dependencies Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: id,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result, id) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error checking dependencies', result);
        toast.error(
          result?.response?.data?.message || result?.message || 'Error checking dependencies'
        );

        mixpanel.track('Check Project Delete Dependencies Failed', {
          user_id: userId,
          company_id: companyIdInt,
          project_id: id,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle success
      const project = FilterProjectData?.find((item) => parseInt(item.id) === parseInt(id));
      const projectName = project?.name || 'Project';

      if (result.canDelete) {
        setCanDelete('yes');
        setResData({ name: projectName });
        setSelectedIds([parseInt(id)]);

        mixpanel.track('Check Project Delete Dependencies Success', {
          user_id: userId,
          company_id: companyIdInt,
          project_id: id,
          project_name: projectName,
          can_delete: true,
          timestamp: new Date().toISOString()
        });
      } else {
        // Fallback if not found
        setResData({ ...result, name: projectName });
        setCanDelete('no');

        mixpanel.track('Check Project Delete Dependencies Success', {
          user_id: userId,
          company_id: companyIdInt,
          project_id: id,
          project_name: projectName,
          can_delete: false,
          dependencies_count: result?.accommodations?.length || 0,
          timestamp: new Date().toISOString()
        });
      }
    },
    onError: (err, id) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');

      mixpanel.track('Check Project Delete Dependencies Failed', {
        user_id: userId,
        company_id: companyIdInt,
        project_id: id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteProject(payload),
    onMutate: (variables) => {
      setIsSubmitting(true); // Disable the button when mutation starts
      mixpanel.track('Delete Project Started', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: variables?.ids,
        is_bulk: variables?.ids?.length > 1,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: (result, variables) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error deleting project', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting project');

        mixpanel.track('Delete Project Failed', {
          user_id: userId,
          company_id: companyIdInt,
          project_ids: variables?.ids,
          is_bulk: variables?.ids?.length > 1,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle success
      toast.success('Project Deleted Successfully');
      refetchProjects();

      mixpanel.track('Delete Project Success', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: variables?.ids,
        is_bulk: variables?.ids?.length > 1,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err, variables) => {
      setIsSubmitting(false);
      console.error('Error deleting project', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting project');

      mixpanel.track('Delete Project Failed', {
        user_id: userId,
        company_id: companyIdInt,
        project_ids: variables?.ids,
        is_bulk: variables?.ids?.length > 1,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };

    mixpanel.track('Delete Project Button Clicked', {
      user_id: userId,
      company_id: companyIdInt,
      project_ids: data,
      is_bulk: data?.length > 1,
      timestamp: new Date().toISOString()
    });

    handleDeleteFn(payload);
  };

  /* 
    JSX
  */

  const { collapsed } = useSidebar();
  return (
    <>
      {isOpenAddProject && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={60}
          name="Add Project"
          onClose={handleToggleAddProjectModal}>
          <AddProject
            onSubmit={handleOnSubmitAddProject}
            upShift={upShift}
            isSubmitting={isSubmitting}
            setUpShift={setUpShift}
            onClose={handleToggleAddProjectModal}
          />
        </Modal>
      )}

      {/* Edit Project Modal */}

      {isOpenEditProject && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={88}
          name="Edit project"
          onClose={handleToggleEditProjectModal}
          upShift={upShift * 10}>
          <EditProject
            id={ProjectStaticId}
            ProjectData={transformedData}
            upShift={upShift}
            isSubmitting={isSubmitting}
            setUpShift={setUpShift}
            onSubmit={handleEditProject}
            onClose={handleToggleEditProjectModal}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this project'}'`}
          onClose={() => setCanDelete(null)}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this project has dependencies. Please resolve the following items to
              proceed.
            </p>
            <div className="flex justify-start items-center flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'project'}
            onClose={() => setCanDelete(null)}
            data={transformedData}
            type={'project'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: 'Company > Projects' }} backArrow={true} />
        </div>

        {/* Cards */}
        <div
          className={`transition-all duration-300 ease-in-out mt-[4rem]  flex flex-col items-center`}>
          <div className="w-full px-6 pt-4 bg-stone-100">
            <div className="flex flex-wrap gap-3 sm:gap-4 max-w-[980px]">
              {metaData?.map((item) => {
                const Icon = item.icon;
                return (
                  <MetricCard
                    key={item._id}
                    title={item.name}
                    value={Number(item.data || 0)}
                    icon={<Icon size={20} strokeWidth={2} />}
                    iconBgClass={item.iconBg}
                    iconColorClass={item.iconColor}
                    valueColorClass={item.valueColor}
                    borderClass={item.border}
                    widthClass="w-[280px] max-w-full shrink-0"
                  />
                );
              })}
            </div>
          </div>
          {/* <Headroom
            upTolerance={0}
            downTolerance={10}
            offset={10}
            style={{
              transition: 'all 0.2s ease-in-out',
              position: 'fixed',
              top: '4rem',
              left: collapsed ? '76px' : '16.6%',
              right: '0',
              zIndex: 2
            }}> */}
          {/* Main content */}

          {/* </Headroom> */}

          {/* Table content */}

          <GridWrapper width={100}>
            <div className="mb-5 rounded-xl border border-[#E7EBF1] bg-white p-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 flex-col gap-3 sm:flex-row">
                  <div className="relative w-full sm:max-w-[280px]">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"
                    />
                    <input
                      type="text"
                      value={projectQuery}
                      onChange={(event) => setProjectQuery(event.target.value)}
                      placeholder="Search project name"
                      className="w-full rounded-lg border border-[#D8DEE7] bg-[#F8FAFC] py-2 pl-9 pr-3 text-sm text-[#334155] outline-none focus:border-[#2A565E]"
                    />
                  </div>

                  <select
                    value={projectTypeFilter}
                    onChange={(event) => setProjectTypeFilter(event.target.value)}
                    className="rounded-lg border border-[#D8DEE7] bg-[#F8FAFC] px-3 py-2 text-sm text-[#334155] outline-none focus:border-[#2A565E]">
                    {projectTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type === 'all' ? 'Project Type' : type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="inline-flex items-center rounded-xl border border-[#DCE3EA] bg-white p-1 shadow-sm w-fit">
                  <button
                    type="button"
                    onClick={() => setViewMode('card')}
                    className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                      viewMode === 'card'
                        ? 'bg-[#EEF2F5] text-[#617087]'
                        : 'text-[#7A879B] hover:text-[#617087]'
                    }`}
                    aria-label="Card view">
                    <LayoutGrid size={14} />
                    <span>Card View</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('table')}
                    className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                      viewMode === 'table'
                        ? 'bg-[#EEF2F5] text-[#0F4D52]'
                        : 'text-[#7A879B] hover:text-[#617087]'
                    }`}
                    aria-label="Table view">
                    <List size={14} />
                    <span>Table View</span>
                  </button>
                </div>
              </div>
            </div>
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                {hasProjects ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        mixpanel.track('Add Project Card Clicked', {
                          user_id: userId,
                          company_id: companyIdInt,
                          timestamp: new Date().toISOString()
                        });
                        handleToggleAddProjectModal();
                      }}
                      className="rounded-2xl border-2 border-dashed border-[#BFD4D8] bg-white text-[#2A565E] flex flex-col items-center justify-center gap-3 shadow-sm hover:bg-[#F7FBFC] hover:border-[#9EC5CC] transition-colors min-h-[220px]">
                      <span className="h-11 w-11 rounded-full bg-[#E8F3F5] flex items-center justify-center">
                        <Plus size={22} />
                      </span>
                      <span className="text-base font-semibold">Add Project</span>
                    </button>
                    {FilterProjectData?.map((item) => {
                      const projectInitials = (item?.name || 'PR')
                        .split(' ')
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0]?.toUpperCase())
                        .join('');
                      const location =
                        [item?.company?.city, item?.company?.state].filter(Boolean).join(', ') ||
                        item?.company?.address ||
                        'Location not added';
                      const hasBudget = Boolean(
                        item?.estimated_budget && !isNaN(Number(item?.estimated_budget))
                      );
                      const budgetLabel = hasBudget
                        ? `Budget: ${Number(item.estimated_budget).toLocaleString()}`
                        : 'Budget: Not added';

                      return (
                        <article
                          key={item?.id}
                          role="link"
                          tabIndex={0}
                          aria-label={`View project: ${item?.name || 'Project'}`}
                          onClick={() => handleNavigateProject(item?.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleNavigateProject(item?.id);
                            }
                          }}
                          className="group bg-white rounded-2xl border border-[#E5EAF1] p-4 flex flex-col gap-2.5 shadow-sm relative cursor-pointer min-h-[220px] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2">
                          <button
                            type="button"
                            aria-label="View project"
                            title="View project"
                            className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#E5EAF1] bg-white text-[#4D6C72] shadow-sm transition-colors hover:bg-[#F7FAFB]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateProject(item?.id);
                            }}>
                            <Eye size={18} strokeWidth={2} />
                          </button>

                          <div className="flex-1 flex flex-col gap-2.5 transition-colors duration-300 rounded-xl group-hover:bg-[#FAFBFC]/80">
                          <div className="flex items-start gap-3 pr-12">
                            <div className="h-14 w-14 rounded-xl border border-[#BFE6DE] bg-[#E7F2EF] text-[#2A565E] flex items-center justify-center text-lg font-semibold">
                              {projectInitials || 'PR'}
                            </div>
                            <div className="min-w-0">
                              <p
                                className="text-[20px] leading-tight font-semibold text-[#111827] truncate"
                                title={item?.name || 'Project Name Not Available'}>
                                {item?.name || 'Project Name Not Available'}
                              </p>
                              <span className="mt-1 inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold bg-[#F3E8FF] text-[#7E22CE]">
                                {item?.project_type || 'Type N/A'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-base text-[#4B5563]">
                            <MapPin size={16} className="text-[#64748B] shrink-0" />
                            <span className="truncate">{location}</span>
                          </div>

                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold ${
                                  hasBudget ? 'bg-[#EAF8F2] text-[#0F766E]' : 'bg-[#F3F4F6] text-[#6B7280]'
                                }`}>
                                <ShieldCheck size={12} />
                                {budgetLabel}
                              </span>
                              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EEF2FF] text-[#4F46E5]">
                                Start: {item?.start_date ? DateFormat(item.start_date) : '---'}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#FFF4E8] text-[#B45309]">
                                End: {item?.completion_date ? DateFormat(item.completion_date) : '---'}
                              </span>
                            </div>
                          </div>
                          </div>
                        </article>
                      );
                    })}
                  </>
                ) : (
                  <div className="rounded-2xl border border-dashed border-[#C7D3DE] bg-white h-[calc(100vh-15.5rem)] flex flex-col items-center justify-center text-center p-6 sm:col-span-2 xl:col-span-3">
                    <p className="text-base font-semibold text-stone-600">No projects found</p>
                    <p className="text-sm text-stone-500 mt-1 mb-3">
                      Add your first project to get started.
                    </p>
                    <Button displayType="primary" onClick={handleToggleAddProjectModal}>
                      Add Project
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[calc(100vh-15.5rem)] overflow-auto rounded-xl border border-[#E7EBF1] bg-white">
                {hasProjects ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#F8FAFC] border-b border-[#E7EBF1] text-[#64748B]">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Project</th>
                          <th className="px-4 py-3 text-left font-semibold">Project Type</th>
                          <th className="px-4 py-3 text-left font-semibold">Estimated budget</th>
                          <th className="px-4 py-3 text-left font-semibold">Start Date</th>
                          <th className="px-4 py-3 text-left font-semibold">Completion Date</th>
                          <th className="px-4 py-3 text-left font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-[#EEF2F6]">
                          <td colSpan={1} className="p-0 flex items-start justify-start">
                            <button
                              type="button"
                              onClick={() => {
                                mixpanel.track('Add Project Table Row Clicked', {
                                  user_id: userId,
                                  company_id: companyIdInt,
                                  timestamp: new Date().toISOString()
                                });
                                handleToggleAddProjectModal();
                              }}
                              className="flex w-full items-center justify-start  gap-2 px-4 py-3 text-sm font-semibold text-[#2A565E] transition-colors bg-[#FAFBFC] hover:bg-[#E8F3F5] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2A565E]">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-[#9EC5CC] bg-white">
                                <Plus size={18} strokeWidth={2.25} />
                              </span>
                              Add Project
                            </button>
                          </td>
                        </tr>
                        {FilterProjectData.map((item) => {
                          const budgetDisplay =
                            item?.estimated_budget != null &&
                            item?.estimated_budget !== '' &&
                            !isNaN(Number(item.estimated_budget))
                              ? formatIndianCurrency(` ${Number(item.estimated_budget)}`)
                              : formatIndianCurrency(' 0');
                          const startDisplay =
                            item?.start_date === '__' ? '---' : DateFormat(item?.start_date);
                          const endDisplay =
                            item?.completion_date === '__'
                              ? '---'
                              : DateFormat(item?.completion_date);

                          return (
                            <tr
                              key={item?.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleNavigateProject(item?.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleNavigateProject(item?.id);
                                }
                              }}
                              className="border-b border-[#EEF2F6] last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-[#0F172A] font-medium">
                                {item?.name || '---'}
                              </td>
                              <td className="px-4 py-3 text-[#334155]">{item?.project_type || '---'}</td>
                              <td className="px-4 py-3 text-[#334155]">{budgetDisplay}</td>
                              <td className="px-4 py-3 text-[#334155]">{startDisplay}</td>
                              <td className="px-4 py-3 text-[#334155]">{endDisplay}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    title="Edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleEditProjectModal(item?.id);
                                    }}
                                    disabled={!item?.id}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] disabled:opacity-50 action-button">
                                    <img src={editIcon} alt="" className="h-5 w-5" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCheckDelete(item?.id);
                                    }}
                                    disabled={!item?.id || isSubmitting}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] disabled:opacity-50 action-button">
                                    <img src={deleteIcon} alt="Delete project" className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col justify-center items-center w-full h-[calc(100vh-15.5rem)] rounded-xl border border-dashed border-[#C7D3DE] bg-white">
                    <p className="text-lg font-semibold text-stone-500 mb-3">
                      No projects found
                    </p>
                    <Button displayType="primary" onClick={handleToggleAddProjectModal}>
                      Add Project
                    </Button>
                  </div>
                )}
              </div>
            )}
          </GridWrapper>
        </div>
      </Layout>
    </>
  );
};

export default ProjectsList;
